export const AdminAutoTopup = {
  scope: {
    statusLabels: {
      loading: 'admin.usage.auto.statusLoading',
      unavailable: 'admin.usage.auto.statusUnavailable',
      off: 'admin.usage.auto.statusOff',
      active: 'admin.usage.auto.statusActive',
      setup_required: 'admin.usage.auto.statusSetupRequired',
      processing: 'admin.usage.auto.statusProcessing',
      limit_reached: 'admin.usage.auto.statusLimitReached',
      payment_failed: 'admin.usage.auto.statusPaymentFailed',
      authentication_required: 'admin.usage.auto.statusAuthenticationRequired',
      reconcile_required: 'admin.usage.auto.statusReconcileRequired',
      unsupported: 'admin.usage.auto.statusUnsupported'
    },
    noticeLabels: {
      loading: 'admin.usage.auto.noticeLoading',
      unavailable: 'admin.usage.auto.noticeUnavailable',
      off: 'admin.usage.auto.noticeOff',
      active: 'admin.usage.auto.noticeActive',
      setup_required: 'admin.usage.auto.noticeSetupRequired',
      processing: 'admin.usage.auto.noticeProcessing',
      limit_reached: 'admin.usage.auto.noticeLimitReached',
      payment_failed: 'admin.usage.auto.noticePaymentFailed',
      authentication_required: 'admin.usage.auto.noticeAuthenticationRequired',
      reconcile_required: 'admin.usage.auto.noticeReconcileRequired',
      unsupported: 'admin.usage.auto.noticeUnsupported'
    },
    validationLabels: {
      rates: 'admin.usage.auto.validationRates',
      amount: 'admin.usage.auto.validationAmount',
      threshold: 'admin.usage.auto.validationThreshold',
      limit: 'admin.usage.auto.validationLimit'
    },
    errorLabels: {
      unavailable: 'admin.usage.auto.errorUnavailable',
      forbidden: 'admin.usage.auto.errorForbidden',
      inProgress: 'admin.usage.auto.errorInProgress',
      failed: 'admin.usage.auto.errorFailed'
    }
  },
  extends: 'AdminCard',
  gap: 'A',
  Header: {
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',
    flexWrap: 'wrap',
    TitleGroup: {
      flow: 'x',
      align: 'center',
      gap: 'Z',
      Emblem: {
        boxSize: 'controlH',
        round: 'radiusPill',
        background: 'title',
        align: 'center center',
        ariaHidden: 'true',
        Icon: { name: 'refresh', color: 'title-reversed', boxSize: 'A' }
      },
      AdminCardTitle: { text: '{{ admin.usage.auto.title | polyglot }}' }
    },
    Status: {
      padding: 'Y Z',
      round: 'radiusPill',
      background: 'overlay',
      fontSize: 'Z',
      fontWeight: '500',
      role: 'status',
      color: (el, s) =>
        ['payment_failed', 'authentication_required', 'reconcile_required'].includes(
          el.call('autoTopupData', s.root)?.status
        )
          ? 'dangerInk'
          : 'title',
      text: (el, s) => {
        const data = el.call('autoTopupData', s.root)
        const status = !data ? 'loading' : data.error ? 'unavailable' : data.status || 'off'
        return el.call(
          'polyglot',
          el.scope.statusLabels[status] || el.scope.statusLabels.unavailable
        )
      }
    }
  },
  AdminCardDesc: { text: '{{ admin.usage.auto.description | polyglot }}' },
  AdminCardDesc_1: {
    role: 'status',
    text: (el, s) => {
      const data = el.call('autoTopupData', s.root)
      const status = !data ? 'loading' : data.error ? 'unavailable' : data.status || 'off'
      return el.call('polyglot', el.scope.noticeLabels[status] || el.scope.noticeLabels.unavailable)
    }
  },
  Rule: {
    hide: (el, s) => !s.root._autoTopupDraft && !el.call('autoTopupData', s.root)?.amountCents,
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: '0',
    padding: 'A',
    round: 'radiusControl',
    background: 'widgetPlate',
    '@mobileL': { gridTemplateColumns: 'minmax(0, 1fr)' },
    '@appMobile': { gridTemplateColumns: 'minmax(0, 1fr)' },
    childExtends: 'AdminAutoTopupMetric',
    childrenAs: 'state',
    children: (el, s) => {
      const rule = el.call('autoTopupRule', s.root)
      return [
        {
          step: '01',
          label: 'threshold',
          value: el.call('autoTopupNumber', rule.thresholdCredits),
          detail: el.call('polyglot', 'admin.usage.auto.below')
        },
        {
          step: '02',
          label: 'refill',
          value: el.call('autoTopupMoney', rule.amountCents),
          detail: el
            .call('polyglot', 'admin.usage.auto.credits')
            .replace('{credits}', el.call('autoTopupNumber', rule.credits))
        },
        {
          step: '03',
          label: 'cap',
          value: el.call('autoTopupMoney', rule.monthlyLimitCents),
          detail: el.call('polyglot', 'admin.usage.auto.perMonth')
        }
      ]
    }
  },
  Budget: {
    hideDeps: ['_autoTopupDraft', '_autoTopup'],
    hide: (el, s) =>
      !el.call('autoTopupData', s.root)?.monthlyLimitCents || !!s.root._autoTopupDraft,
    flow: 'y',
    gap: 'Y',
    Label: {
      fontSize: 'Z',
      color: 'caption',
      text: (el, s) => {
        const data = el.call('autoTopupData', s.root)
        return el
          .call('polyglot', 'admin.usage.auto.spent')
          .replace('{spent}', el.call('autoTopupMoney', data?.spentCents))
          .replace('{limit}', el.call('autoTopupMoney', data?.monthlyLimitCents))
      }
    },
    ProgressBar: {
      height: 'Y',
      round: 'radiusPill',
      value: (el, s) => {
        const data = el.call('autoTopupData', s.root)
        return data?.monthlyLimitCents > 0
          ? Math.min(1, data.spentCents / data.monthlyLimitCents)
          : 0
      },
      'aria-label': '{{ admin.usage.auto.budget | polyglot }}',
      'aria-valuemin': 0,
      'aria-valuemax': (el, s) => el.call('autoTopupData', s.root)?.monthlyLimitCents / 100 || 0,
      'aria-valuenow': (el, s) => {
        const data = el.call('autoTopupData', s.root)
        return Math.min(data?.monthlyLimitCents || 0, data?.spentCents || 0) / 100
      },
      Fill: {
        round: 'radiusPill',
        background: 'title',
        width: (el, s) => {
          const data = el.call('autoTopupData', s.root)
          return `${data?.monthlyLimitCents > 0 ? Math.min(100, Math.max(0, (data.spentCents / data.monthlyLimitCents) * 100)) : 0}%`
        }
      }
    },
    Card: {
      fontSize: 'Z',
      color: 'caption',
      text: (el, s) => {
        const data = el.call('autoTopupData', s.root)
        return data?.card
          ? el
              .call('polyglot', 'admin.usage.auto.card')
              .replace('{brand}', data.card.brand)
              .replace('{last4}', data.card.last4)
          : ''
      }
    }
  },
  Editor: {
    hide: (el, s) => s.root._autoTopupStep !== 'edit',
    flow: 'y',
    gap: 'Z',
    Fields: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, var(--spacing-F)), 1fr))',
      gap: 'Z',
      childExtends: 'AdminAutoTopupField',
      childrenAs: 'state',
      children: [
        { field: 'thresholdCredits', label: 'triggerLabel', step: '1' },
        { field: 'amountUsd', label: 'amountLabel', step: '0.01' },
        { field: 'limitUsd', label: 'limitLabel', step: '0.01' }
      ]
    },
    AdminCardDesc: {
      text: (el, s) => {
        const rates = s.root._usageRates?.data || s.root._usageRates || {}
        return el
          .call('polyglot', 'admin.usage.auto.rateHint')
          .replace('{rate}', el.call('autoTopupMoney', rates.centsPerCredit))
          .replace('{min}', el.call('autoTopupMoney', rates.topupMinCents))
          .replace('{max}', el.call('autoTopupMoney', rates.topupMaxCents))
      }
    },
    Validation: {
      role: 'alert',
      color: 'dangerInk',
      fontSize: 'Z',
      hide: (el, s) => !s.root._autoTopupTouched || !el.call('autoTopupValidation', s.root),
      text: (el, s) =>
        el.call(
          'polyglot',
          el.scope.validationLabels[el.call('autoTopupValidation', s.root)] ||
            el.scope.validationLabels.rates
        )
    },
    Actions: {
      flow: 'x',
      gap: 'Z',
      flexWrap: 'wrap',
      AdminActionBtnPrimary: {
        text: '{{ admin.usage.auto.review | polyglot }}',
        onClick: (e, el, s) => el.call('autoTopupReview', s.root)
      },
      AdminActionBtn: {
        text: '{{ admin.usage.auto.cancel | polyglot }}',
        onClick: (e, el, s) => el.call('autoTopupClose', s.root)
      }
    }
  },
  Review: {
    hide: (el, s) => s.root._autoTopupStep !== 'review',
    flow: 'y',
    gap: 'Z',
    AdminCardTitle: { tag: 'h3', text: '{{ admin.usage.auto.reviewTitle | polyglot }}' },
    AdminCardDesc: { text: '{{ admin.usage.auto.reviewDescription | polyglot }}' },
    CheckboxField: {
      minHeight: 'controlH',
      align: 'start',
      gap: 'Z',
      Input: {
        checked: (el, s) => !!s.root._autoTopupConsent,
        disabled: (el, s) => !!s.root._autoTopupBusy,
        onChange: (e, el, s) => s.root.update({ _autoTopupConsent: e.target.checked })
      },
      Span: {
        opacity: '1',
        color: 'paragraph',
        fontSize: 'Z2',
        text: '{{ admin.usage.auto.consent | polyglot }}'
      }
    },
    Actions: {
      flow: 'x',
      gap: 'Z',
      flexWrap: 'wrap',
      AdminActionBtnPrimary: {
        text: (el, s) =>
          el.call(
            'polyglot',
            s.root._autoTopupBusy ? 'admin.usage.auto.wait' : 'admin.usage.auto.prepare'
          ),
        disabled: (el, s) => !!s.root._autoTopupBusy || !s.root._autoTopupConsent,
        onClick: (e, el, s) => el.call('autoTopupAction', s.root, 'setup')
      },
      AdminActionBtn: {
        text: '{{ admin.usage.auto.back | polyglot }}',
        disabled: (el, s) => !!s.root._autoTopupBusy,
        onClick: (e, el, s) => s.root.update({ _autoTopupStep: 'edit', _autoTopupConsent: false })
      }
    }
  },
  SecureCard: {
    hide: (el, s) => s.root._autoTopupStep !== 'card',
    flow: 'y',
    gap: 'Z',
    AdminCardDesc: { text: '{{ admin.usage.auto.secureHint | polyglot }}' },
    Actions: {
      flow: 'x',
      gap: 'Z',
      flexWrap: 'wrap',
      AdminActionBtnPrimary: {
        tag: 'a',
        type: null,
        href: (el, s) => s.root._autoTopupCheckoutUrl || undefined,
        textDecoration: 'none',
        align: 'center center',
        text: '{{ admin.usage.auto.continue | polyglot }}'
      },
      AdminActionBtn: {
        text: '{{ admin.usage.auto.cancel | polyglot }}',
        disabled: (el, s) => !!s.root._autoTopupBusy,
        onClick: (e, el, s) => el.call('autoTopupAction', s.root, 'pause')
      }
    }
  },
  Error: {
    hide: (el, s) => !s.root._autoTopupError,
    role: 'alert',
    fontSize: 'Z',
    color: 'dangerInk',
    text: (el, s) =>
      el.call(
        'polyglot',
        el.scope.errorLabels[s.root._autoTopupError] || el.scope.errorLabels.failed
      )
  },
  Actions: {
    hide: (el, s) => !!s.root._autoTopupStep,
    flow: 'x',
    gap: 'Z',
    flexWrap: 'wrap',
    AdminActionBtnPrimary: {
      hide: (el, s) => {
        const data = el.call('autoTopupData', s.root)
        return (
          !data ||
          data.error ||
          data.supported === false ||
          ['processing', 'reconcile_required'].includes(data.status)
        )
      },
      disabled: (el, s) => !!s.root._autoTopupBusy,
      text: (el, s) =>
        el.call(
          'polyglot',
          el.call('autoTopupData', s.root)?.enabled
            ? 'admin.usage.auto.edit'
            : 'admin.usage.auto.setup'
        ),
      onClick: (e, el, s) => el.call('autoTopupEdit', s.root)
    },
    AdminActionBtn: {
      hide: (el, s) => {
        const data = el.call('autoTopupData', s.root)
        return !data?.enabled && data?.status !== 'setup_required'
      },
      disabled: (el, s) => !!s.root._autoTopupBusy,
      text: '{{ admin.usage.auto.pause | polyglot }}',
      onClick: (e, el, s) => el.call('autoTopupAction', s.root, 'pause')
    },
    AdminActionBtn_1: {
      disabled: (el, s) => !!s.root._autoTopupBusy,
      text: (el, s) =>
        el.call(
          'polyglot',
          s.root._autoTopupBusy ? 'admin.usage.auto.wait' : 'admin.usage.auto.refresh'
        ),
      onClick: (e, el, s) =>
        el.call(
          'autoTopupAction',
          s.root,
          el.call('autoTopupData', s.root)?.status === 'setup_required' ? 'refresh' : 'load'
        )
    }
  }
}

export const AdminAutoTopupMetric = {
  scope: {
    metricLabels: {
      threshold: 'admin.usage.auto.metricThreshold',
      refill: 'admin.usage.auto.metricRefill',
      cap: 'admin.usage.auto.metricCap'
    }
  },
  flow: 'y',
  gap: 'Z',
  padding: '0 A 0 0',
  minWidth: '0',
  '@mobileL': { display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', padding: '0 0 A' },
  '@appMobile': { display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', padding: '0 0 A' },
  ':last-child': { padding: '0' },
  Header: {
    flow: 'x',
    align: 'center',
    gap: 'Z',
    '@mobileL': { flow: 'y', gridRow: '1 / 4' },
    '@appMobile': { flow: 'y', gridRow: '1 / 4' },
    Step: {
      boxSize: 'B',
      round: 'radiusPill',
      background: 'title',
      color: 'title-reversed',
      align: 'center center',
      fontSize: 'Y',
      flexShrink: '0',
      fontVariantNumeric: 'tabular-nums',
      text: (el, s) => s.step
    },
    Connector: {
      flex: '1',
      height: 'hairline',
      background: 'line',
      ariaHidden: 'true',
      visibility: (el, s) => (s.step === '03' ? 'hidden' : 'visible'),
      '@mobileL': { width: 'hairline', height: 'auto' },
      '@appMobile': { width: 'hairline', height: 'auto' }
    }
  },
  Label: {
    fontSize: 'Z',
    fontWeight: '500',
    color: 'caption',
    text: (el, s) =>
      el.call('polyglot', el.scope.metricLabels[s.label] || el.scope.metricLabels.threshold)
  },
  Value: {
    fontSize: 'D',
    fontWeight: '700',
    color: 'title',
    lineHeight: '1.1',
    fontVariantNumeric: 'tabular-nums',
    overflowWrap: 'anywhere',
    text: (el, s) => s.value
  },
  Detail: { fontSize: 'Z', color: 'caption', text: (el, s) => s.detail }
}

export const AdminAutoTopupField = {
  scope: {
    fieldLabels: {
      triggerLabel: 'admin.usage.auto.triggerLabel',
      amountLabel: 'admin.usage.auto.amountLabel',
      limitLabel: 'admin.usage.auto.limitLabel'
    }
  },
  extends: 'AppField',
  minWidth: '0',
  label: (el, s) =>
    el.call('polyglot', el.scope.fieldLabels[s.label] || el.scope.fieldLabels.triggerLabel),
  AppInput: {
    type: 'number',
    inputMode: 'decimal',
    step: (el, s) => s.step,
    value: (el, s) => s.root._autoTopupDraft?.[s.field] ?? '',
    onInput: (e, el, s) => el.call('autoTopupChange', s.root, s.field, e.target.value)
  }
}
